import { useState, useEffect, useRef, useCallback } from "react";
import { Modal } from "antd";
import { WarningOutlined } from "@ant-design/icons";

const HEARTBEAT_INTERVAL = 5000;
const STATE_SAVE_INTERVAL = 5000;

// Keys to persist for crash recovery
const SESSION_KEYS = ["cart", "currentScreen", "loggedInUser", "tillSession"];

function CrashRecoveryProvider({ children, getSessionState, onRestore }) {
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveredState, setRecoveredState] = useState(null);
  const [checked, setChecked] = useState(false);
  const heartbeatRef = useRef(null);
  const stateSaveRef = useRef(null);

  const isElectron = typeof window !== "undefined" && window.electronAPI?.session;

  // Check if recovery is needed on mount
  useEffect(() => {
    if (!isElectron) {
      setChecked(true);
      return;
    }

    async function checkRecovery() {
      try {
        const needed = await window.electronAPI.session.isRecoveryNeeded();
        if (needed) {
          const allState = await window.electronAPI.session.loadAll();
          setRecoveredState(allState);
          setShowRecovery(true);
        } else {
          setChecked(true);
        }
      } catch (err) {
        console.error("[CrashRecovery] Error checking recovery:", err);
        setChecked(true);
      }
    }

    checkRecovery();
  }, [isElectron]);

  // Start heartbeat + state saving once checked
  useEffect(() => {
    if (!isElectron || !checked) return;

    // Heartbeat
    heartbeatRef.current = setInterval(() => {
      window.electronAPI.session.heartbeat().catch(() => {});
    }, HEARTBEAT_INTERVAL);

    // Periodic state save
    stateSaveRef.current = setInterval(() => {
      if (typeof getSessionState === "function") {
        const state = getSessionState();
        for (const key of SESSION_KEYS) {
          if (state[key] !== undefined) {
            window.electronAPI.session.save(key, state[key]).catch(() => {});
          }
        }
      }
    }, STATE_SAVE_INTERVAL);

    // Send initial heartbeat
    window.electronAPI.session.heartbeat().catch(() => {});

    return () => {
      clearInterval(heartbeatRef.current);
      clearInterval(stateSaveRef.current);
    };
  }, [isElectron, checked, getSessionState]);

  const handleRestore = useCallback(() => {
    if (typeof onRestore === "function" && recoveredState) {
      onRestore(recoveredState);
    }
    setShowRecovery(false);
    setChecked(true);
  }, [onRestore, recoveredState]);

  const handleStartFresh = useCallback(async () => {
    try {
      await window.electronAPI.session.clear();
    } catch (err) {
      console.error("[CrashRecovery] Error clearing session:", err);
    }
    setShowRecovery(false);
    setChecked(true);
  }, []);

  if (showRecovery) {
    return (
      <>
        <Modal visible footer={null} closable={false} width={450} centered styles={{ body: { padding: "32px", fontFamily: "Inter, sans-serif" } }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "24px" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                backgroundColor: "#FFF3CD",
                borderRadius: "999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningOutlined style={{ color: "#856404", fontSize: "20px" }} />
            </div>
          </div>

          <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#101828", marginBottom: "12px", textAlign: "center" }}>Session Recovery</h2>

          <p style={{ fontSize: "14px", color: "#475467", marginBottom: "24px", textAlign: "center" }}>
            Your last session ended unexpectedly. Would you like to restore your previous session?
          </p>

          <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
            <button
              onClick={handleStartFresh}
              style={{
                backgroundColor: "#F3F4F6",
                border: "1px solid #D1D5DB",
                color: "#374151",
                padding: "8px 20px",
                fontSize: "14px",
                fontWeight: 500,
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Start Fresh
            </button>
            <button
              onClick={handleRestore}
              style={{
                backgroundColor: "#2970FF",
                border: "none",
                color: "#FFFFFF",
                padding: "8px 20px",
                fontSize: "14px",
                fontWeight: 500,
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Restore Session
            </button>
          </div>
        </Modal>
      </>
    );
  }

  if (!checked && isElectron) {
    return null; // Still checking, don't render children yet
  }

  return children;
}

export default CrashRecoveryProvider;
